import type { FacadeColorType, FacadeType, LiftType, ShelvingCellConfig, WardrobeModule } from "../types";
import {
  getShelvingCellLayouts,
  getShelvingGridLayout,
  shelvingCellFacadeRect,
  shelvingDrawerCount,
  drawerLimits,
  shelvingFacadeOverhangMm,
  SHELVING_DRAWER_MIN_FRONT_MM,
  SHELVING_DRAWER_SEAM_MM,
  SHELVING_PANEL_THICKNESS_MM,
  type ShelvingCellLayout,
  type ShelvingFacadeRect,
  type ShelvingGridLayout
} from "./shelving";
import { FLAP_DOWN_MAX_HEIGHT_MM, FLAP_DOWN_MIN_HEIGHT_MM, SINGLE_HINGED_FRONT_MAX_WIDTH_MM } from "./flapFronts";
import { decorLabel, resolveDecor } from "./decors";
import { eggerDecor } from "./eggerDecors";
import { facadePaintLabel } from "./facades";

/**
 * Комод (замечания Сергея 15.10): колонки — ящики или фасад (дверца, две
 * дверцы, откидной), стеклянные дверцы, эмалевый цоколь в цвет фасадов,
 * столешница в своём декоре, свес фасадов и покраска ящиков внутри.
 */

export const isChest = (module: Pick<WardrobeModule, "type" | "shelvingKind" | "stackedOnId">) =>
  module.type === "shelving" && module.shelvingKind === "chest" && !module.stackedOnId;

/** Стеклянная дверца колонки: свою отделку комода она не принимает. */
export const isChestGlassDoor = (cell: Pick<ShelvingCellConfig, "content" | "facade">) =>
  cell.content === "door" && cell.facade === "glass";

/** Колонка — образец общей отделки комода: первая с ящиками или нестеклянной дверцей. */
export const chestFinishCell = (module: WardrobeModule) =>
  (module.shelvingCells ?? []).find((cell) =>
    (cell.content === "drawers" || cell.content === "door") && !isChestGlassDoor(cell));

const isEnamel = (facade: FacadeType | undefined) => facade === "satin" || facade === "gloss";

/** Фасады комода в эмали: тогда цоколь тоже эмалевый, без фрезеровки. */
export const chestEnamelFinish = (module: WardrobeModule) => {
  if (!isChest(module)) return null;
  const cell = chestFinishCell(module);
  return cell && isEnamel(cell.facade) ? cell : null;
};

/** Цоколь комода в эмали — в цвет фасадов (у пола и на ножках цоколя нет). */
export const chestPlinthEnamel = (module: WardrobeModule) =>
  module.shelvingBase !== "floor" && module.shelvingBase !== "legs" ? chestEnamelFinish(module) : null;

/** Цвет покраски ящиков комода: свой или «в цвет фасадов». */
export interface ChestDrawerPaint {
  facadeColor?: FacadeColorType;
  facadeColorHex?: string;
  facadeColorCode?: string;
  /** Цвет взят у фасадов в эмали, своего не задано. */
  sameAsFacades: boolean;
}

/** Задан ли у ящиков свой цвет покраски. */
export const chestDrawerHasOwnPaint = (module: WardrobeModule) =>
  Boolean(module.chestDrawerPaintColor || module.chestDrawerPaintHex || module.chestDrawerPaintCode);

/**
 * Ящики покрашены внутри — при любых фасадах (15.10). Цвет: свой, а если не
 * задан — фасадов в эмали; у фасадов ЛДСП — белый.
 */
export const chestDrawerPaintFinish = (module: WardrobeModule): ChestDrawerPaint | null => {
  if (!isChest(module) || !module.chestDrawerPaint) return null;
  const enamel = chestEnamelFinish(module);
  if (!chestDrawerHasOwnPaint(module) && enamel) {
    return { facadeColor: enamel.facadeColor, facadeColorHex: enamel.facadeColorHex, facadeColorCode: enamel.facadeColorCode, sameAsFacades: true };
  }
  return { facadeColor: module.chestDrawerPaintColor ?? "white", facadeColorHex: module.chestDrawerPaintHex,
    facadeColorCode: module.chestDrawerPaintCode, sameAsFacades: false };
};

/** Подпись для техкарты и PDF: «в цвет фасадов (Шалфей)» или «Графит». */
export const chestDrawerPaintLabel = (paint: ChestDrawerPaint) =>
  paint.sameAsFacades ? `в цвет фасадов (${facadePaintLabel(paint)})` : facadePaintLabel(paint);

/**
 * Поля цвета покраски ящиков после проверки. Опцию «Покрасить ящики внутри»
 * Сергей убрал (видео 24.10) — в проектах она больше не хранится.
 */
export const normalizeChestDrawerPaint = (_module: WardrobeModule, _chest: boolean) => ({
  chestDrawerPaint: undefined,
  chestDrawerPaintColor: undefined,
  chestDrawerPaintHex: undefined,
  chestDrawerPaintCode: undefined
});

/** Декор столешницы комода, если он свой. */
export const normalizeTopDecor = (module: Pick<WardrobeModule, "type" | "shelvingKind" | "stackedOnId" | "topDecor">) =>
  isChest(module) && eggerDecor(module.topDecor) ? eggerDecor(module.topDecor)!.code : undefined;

/** Подпись для техкарты и PDF. */
export const topDecorDescription = (module: WardrobeModule) => {
  const own = normalizeTopDecor(module);
  return own ? `${decorLabel(resolveDecor(own))} · свой декор столешницы` : null;
};

/** Отделка колонки «как у комода»: материал, декор, цвет, фрезеровка и ручка образца. */
export const chestFinishPatch = (finish: ShelvingCellConfig): Partial<ShelvingCellConfig> => ({
  facade: finish.facade ?? "wood",
  facadePattern: finish.facadePattern,
  facadeDecor: finish.facadeDecor,
  facadeMaterial: finish.facadeMaterial,
  facadeColor: finish.facadeColor,
  facadeColorHex: finish.facadeColorHex,
  facadeColorCode: finish.facadeColorCode,
  facadePaintSides: finish.facadePaintSides,
  handle: finish.handle
});

// ---------------------------------------------------------------------------
// Колонка «ящики и откидной сверху» и подбор открывания фасада (16.10)

/** Верхний откидной комода — Aventos HK top: фасад 205–600 мм, поворачивается вверх перед столешницей. */
export const CHEST_TOP_FLAP_MIN_MM = 205;
export const CHEST_TOP_FLAP_MAX_MM = 600;
export const CHEST_TOP_FLAP_DEFAULT_MM = 300;
/** Над корпусом верхний откидной не поднимаем (HL, HS) и не складываем (HF): только HK top. */
export const CHEST_TOP_FLAP_EXCLUDED_LIFTS: readonly LiftType[] = ["HL", "HS", "HF"];

type CellPlace = Pick<ShelvingCellLayout, "column" | "row" | "columnSpan" | "rowSpan" | "left" | "bottom" | "width" | "height">;

/**
 * Высота верхнего откидного: 205–600 мм и так, чтобы снизу поместился хотя бы
 * один ящик (фасад от 250 мм, у комода со свесом — плюс свес). null — не помещается.
 */
export const chestTopFlapRange = (module: WardrobeModule, layout: CellPlace, grid?: ShelvingGridLayout) => {
  const rect = shelvingCellFacadeRect(module, layout, grid);
  const max = Math.min(CHEST_TOP_FLAP_MAX_MM,
    rect.height - SHELVING_DRAWER_SEAM_MM - SHELVING_DRAWER_MIN_FRONT_MM - shelvingFacadeOverhangMm(module));
  return max >= CHEST_TOP_FLAP_MIN_MM ? { min: CHEST_TOP_FLAP_MIN_MM, max: Math.floor(max) } : null;
};

export interface ChestCellSplit {
  /** Высота откидного фасада, мм. */
  flapHeightMm: number;
  flapRect: ShelvingFacadeRect;
  /** Фасады ящиков вместе — ниже шва над полкой. */
  drawersRect: ShelvingFacadeRect;
  /** Полка между частями: центр, мм от низа проёма. */
  shelfY: number;
  /** Проём под ящиками в свету, мм. */
  drawersClearMm: number;
  /** Отсек над полкой в свету, мм. */
  flapClearMm: number;
}

/** Колонка комода из двух частей: сверху откидной вверх, снизу ящики; null — обычная колонка. */
export const chestCellSplit = (
  module: WardrobeModule,
  cell: Pick<ShelvingCellConfig, "content" | "chestTopFlap">,
  layout: CellPlace,
  grid?: ShelvingGridLayout
): ChestCellSplit | null => {
  if (!isChest(module) || cell.content !== "drawers" || !cell.chestTopFlap) return null;
  const range = chestTopFlapRange(module, layout, grid);
  if (!range) return null;
  const rect = shelvingCellFacadeRect(module, layout, grid);
  const flapHeightMm = Math.min(range.max, Math.max(range.min, Math.round(cell.chestTopFlap)));
  const top = rect.bottom + rect.height;
  const shelfCenter = top - flapHeightMm - SHELVING_DRAWER_SEAM_MM / 2;
  const shelfY = shelfCenter - layout.bottom;
  return {
    flapHeightMm,
    flapRect: { ...rect, bottom: top - flapHeightMm, height: flapHeightMm },
    drawersRect: { ...rect, height: shelfCenter - SHELVING_DRAWER_SEAM_MM / 2 - rect.bottom },
    shelfY,
    drawersClearMm: shelfY - SHELVING_PANEL_THICKNESS_MM / 2,
    flapClearMm: layout.height - shelfY - SHELVING_PANEL_THICKNESS_MM / 2
  };
};

/** Число ящиков ячейки: по проёму под ящики (у колонки с откидным — ниже полки). */
export const shelvingCellDrawerCount = (
  module: WardrobeModule,
  cell: Pick<ShelvingCellConfig, "content" | "chestTopFlap" | "drawerCount">,
  layout: CellPlace,
  grid?: ShelvingGridLayout
) => {
  const split = chestCellSplit(module, cell, layout, grid);
  return shelvingDrawerCount(cell.drawerCount, split ? split.drawersClearMm : layout.height, drawerLimits(module));
};

/** То же по id ячейки — там, где под рукой только ячейка. */
export const shelvingCellDrawerCountById = (module: WardrobeModule, cell: ShelvingCellConfig) => {
  const grid = getShelvingGridLayout(module);
  const layout = getShelvingCellLayouts(module, grid).find((candidate) => candidate.id === cell.id);
  return layout
    ? shelvingCellDrawerCount(module, cell, layout, grid)
    : shelvingDrawerCount(cell.drawerCount, grid.cellHeights[cell.row] ?? 0, drawerLimits(module));
};

/**
 * Открывание фасада колонки по его размеру (вариант 2, 16.10): до 600 мм —
 * откидной вниз (низкий комод, тумба под ТВ), выше — на петлях: одна дверца
 * до 600 мм шириной, шире — две.
 */
export const chestAutoFrontOpening = (widthMm: number, heightMm: number): Pick<ShelvingCellConfig, "doorOpening"> =>
  heightMm >= FLAP_DOWN_MIN_HEIGHT_MM && heightMm <= FLAP_DOWN_MAX_HEIGHT_MM
    ? { doorOpening: "down" }
    : widthMm > SINGLE_HINGED_FRONT_MAX_WIDTH_MM + 0.5 ? { doorOpening: "double" } : { doorOpening: undefined };

/** Подобрать открывание фасадам колонок, где его не выбрали вручную. */
export const withChestAutoFronts = <T extends WardrobeModule>(module: T): T => {
  if (!isChest(module) || !module.shelvingCells?.some((cell) => cell.content === "door" && !cell.chestFrontManual)) return module;
  const grid = getShelvingGridLayout(module);
  const layouts = new Map(getShelvingCellLayouts(module, grid).map((layout) => [layout.id, layout]));
  return {
    ...module,
    shelvingCells: module.shelvingCells.map((cell) => {
      const layout = layouts.get(cell.id);
      if (cell.content !== "door" || cell.chestFrontManual || !layout) return cell;
      const rect = shelvingCellFacadeRect(module, layout, grid);
      return { ...cell, ...chestAutoFrontOpening(rect.width, rect.height) };
    })
  };
};
